import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LayoutDashboard, MailCheck, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success('If that email exists, a reset link has been sent.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="forgot-password-page">
      {/* Left - Brand */}
      <div
        className="hidden lg:flex lg:w-[45%] flex-col justify-between p-14 relative overflow-hidden"
        style={{ background: '#0A3622' }}
      >
        <div className="relative z-10">
          <div className="flex items-center gap-2.5 mb-16">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.15)' }}
            >
              <LayoutDashboard size={18} className="text-white" />
            </div>
            <span className="font-heading text-lg font-semibold text-white">SnuggleMind</span>
          </div>
          <h2 className="font-heading text-4xl font-bold text-white leading-tight mb-5">
            Forgot your<br />
            <span style={{ color: '#D98A4A' }}>password?</span>
          </h2>
          <p
            className="text-[15px] font-body leading-relaxed max-w-sm"
            style={{ color: 'rgba(255,255,255,0.7)' }}
          >
            No worries — enter the email associated with your SnuggleMind account and
            we'll send a secure link to reset it. The link expires in one hour.
          </p>
        </div>
        <div
          className="absolute top-0 right-0 w-[500px] h-[500px] opacity-[0.08]"
          style={{ background: 'radial-gradient(circle, #D98A4A 0%, transparent 70%)' }}
        />
      </div>

      {/* Right - Form */}
      <div
        className="w-full lg:w-[55%] flex flex-col justify-center px-8 lg:px-16 xl:px-20"
        style={{ background: '#F7F6F2' }}
      >
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-2.5 mb-10 lg:hidden">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: '#0A3622' }}
            >
              <LayoutDashboard size={18} className="text-white" />
            </div>
            <span className="font-heading text-lg font-semibold" style={{ color: '#1A1A1A' }}>
              SnuggleMind
            </span>
          </div>

          {sent ? (
            <div className="text-center" data-testid="forgot-password-sent">
              <div
                className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-5"
                style={{ background: 'rgba(45,122,93,0.12)' }}
              >
                <MailCheck size={26} style={{ color: '#2D7A5D' }} />
              </div>
              <h1
                className="font-heading text-3xl font-bold tracking-tight mb-2"
                style={{ color: '#1A1A1A' }}
              >
                Check your inbox
              </h1>
              <p className="font-body text-[15px] mb-1" style={{ color: '#4B5563' }}>
                If an account exists for
              </p>
              <p
                className="font-body text-[15px] font-semibold mb-6"
                style={{ color: '#0A3622' }}
                data-testid="forgot-password-email-echo"
              >
                {email}
              </p>
              <p className="font-body text-[14px] leading-relaxed mb-8" style={{ color: '#4B5563' }}>
                you'll receive a password reset link within a few minutes. The link expires in one hour.
                Be sure to check your spam folder.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-lg font-body font-medium text-white text-[14px]"
                style={{ background: '#0A3622' }}
                data-testid="forgot-password-back-to-login"
              >
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1
                className="font-heading text-3xl font-bold tracking-tight mb-1.5"
                style={{ color: '#1A1A1A' }}
              >
                Reset your password
              </h1>
              <p className="font-body text-[15px] mb-7" style={{ color: '#4B5563' }}>
                Enter the email on your account and we'll send a reset link.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label
                    htmlFor="email"
                    className="text-[12px] font-body font-medium"
                    style={{ color: '#4B5563' }}
                  >
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    data-testid="forgot-password-email-input"
                    className="h-11 rounded-lg font-body text-[14px]"
                    style={{ borderColor: '#E5E3DB', background: '#FFFFFF' }}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-password-submit-button"
                  className="w-full h-11 rounded-lg font-body font-medium text-white text-[14px]"
                  style={{ background: '#0A3622' }}
                >
                  {loading ? 'Sending link…' : 'Send reset link'}
                </Button>
              </form>

              <p
                className="mt-6 text-center text-[13px] font-body"
                style={{ color: '#4B5563' }}
              >
                Remembered it?{' '}
                <Link
                  to="/login"
                  className="font-medium hover:underline"
                  style={{ color: '#0A3622' }}
                  data-testid="forgot-password-signin-link"
                >
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
